// Direct Mixpanel integration for the paywall_popup flow ONLY.
//
// Why this exists
// ---------------
// Every other prelander flow redirects the user to the platform after signup,
// and the backend already emits `registration_started` at /auth/pre-lander time
// (see backend auth.service.ts processRegisterExternalPrelander). The
// `paywall_popup` flow is different: after signup the paywall is rendered
// IN PLACE (createAccount.jsx returns <PaywallPopup> instead of redirecting), so
// a user who registers but never purchases never leaves the LP. This module lets
// the LP emit `registration_started` directly so that cohort isn't lost.
//
// "Count once" guarantee
// ----------------------
// We identify the user by their Mongo _id and stamp a DETERMINISTIC $insert_id
// (see buildInsertId below) — IDENTICAL to the scheme used by:
//   - the backend  : backend/src/vendors/mixpanel/insert-id.ts
//   - the platform : frontend/src/lib/mixpanel/insertId.ts
// Mixpanel drops events sharing (event name, distinct_id, $insert_id) within
// ~5 days, so even after the user purchases and lands on the platform, this
// event is counted EXACTLY ONCE regardless of how many sides send it. No
// platform-side condition is needed — the shared $insert_id does the deduping.

import mixpanel from 'mixpanel-browser';
import { getLanderContent } from './analytics';

// Provide these via the LP env (CRA REACT_APP_* prefix). Host defaults to the EU
// ingestion endpoint, matching the platform (lib/mixpanel/mixpanel.ts).
const TOKEN = process.env.REACT_APP_MIXPANEL_PROJECT_TOKEN || '';
const HOST =
  process.env.REACT_APP_MIXPANEL_PROJECT_HOST || 'https://api-eu.mixpanel.com';

// Mirrors api.js / analytics.js LP_SOURCE so this LP's registrations can be told
// apart from the other landers in Mixpanel.
const LP_SOURCE = process.env.REACT_APP_VERCEL_ENV ? 'createsexfriend' : 'swipey.ai/createsexfriend';
const REGISTRATION_STARTED = 'registration_started';
const REGISTRATION_COMPLETED = 'registration_completed';
const ORDER_CREATED = 'order_created';

// The experiment flags the ticket requires ON the order_created EVENT (so the
// event is filterable by A/B arm directly, not only via the profile). Only these
// go on the event payload — spreading the whole ab_testing map there would also
// overwrite the explicit lp_source / lander-content props with ab_testing's own
// copies and bloat the checkout event with unrelated flags.
const ORDER_CREATED_EVENT_EXPERIMENT_KEYS = [
  'createsexfriend-paywall-nav-config',
  'createsexfriend-paywall-order',
];

// Deterministic $insert_id for the events emitted by more than one sender.
//
// Why not `${userId}-${eventName}`: Mixpanel constrains $insert_id to at most
// 36 bytes made only of alphanumeric characters or `-` (and none of the reserved
// invalid ids listed for distinct_id). That scheme broke two of those rules —
// `68b3f0a1c2d4e5f60718293a-registration_started` is 45 bytes and contains `_` —
// so it was not a usable dedupe key and both the LP and the backend copy of one
// registration could be counted.
//
// Scheme: `${userId}-${code}` with a short alphanumeric per-event code, i.e. 27
// bytes for a 24-character Mongo ObjectId. The event name doesn't need to be in
// the id: Mixpanel's dedupe key is (event name, distinct_id, $insert_id), so the
// code only has to keep the funnel steps apart within one user.
//
// KEEP IN SYNC with the two other senders — a code that differs on one side
// re-splits the dedupe:
//   - backend  : backend/src/vendors/mixpanel/insert-id.ts
//   - platform : frontend/src/lib/mixpanel/insertId.ts
const INSERT_ID_EVENT_CODES = {
  [REGISTRATION_STARTED]: 'rs',
  [REGISTRATION_COMPLETED]: 'rc',
};
const MAX_INSERT_ID_LENGTH = 36;

// Returns null when the event isn't one of the deduped ones or there's no usable
// user id — the caller then omits $insert_id entirely rather than sending one
// Mixpanel would reject.
export const buildInsertId = (userId, eventName) => {
  const code = INSERT_ID_EVENT_CODES[eventName];
  if (!userId || !code) return null;
  // Strip anything outside Mixpanel's allowed charset, then leave room for
  // `-code`. A Mongo _id is 24 hex characters so neither step changes it in
  // practice; truncating the user part (not the whole string) keeps the code.
  const safeUserId = String(userId)
    .replace(/[^a-zA-Z0-9-]/g, '')
    .slice(0, MAX_INSERT_ID_LENGTH - code.length - 1);
  if (!safeUserId) return null;
  return `${safeUserId}-${code}`;
};

let initialized = false;
let warnedNoToken = false;
// Last /engage payload sent (see sendProfileUpdate) — guards against sending the
// identical profile update twice within one page life.
let lastProfilePayload = '';

// Init once, lazily on first use — non-paywall_popup visitors never spin up a
// Mixpanel session. Config mirrors the platform's mixpanel.init.
const ensureInit = () => {
  if (initialized) return true;
  if (!TOKEN) {
    // Loud one-time signal: without the token the whole Mixpanel deliverable is
    // a silent no-op. Make a missing env var observable instead of invisible.
    if (!warnedNoToken) {
      warnedNoToken = true;
      console.warn(
        '[mixpanel] REACT_APP_MIXPANEL_PROJECT_TOKEN is not set — LP Mixpanel tracking is disabled.',
      );
    }
    return false;
  }
  mixpanel.init(TOKEN, {
    api_host: HOST,
    track_pageview: false,
    persistence: 'localStorage',
  });
  initialized = true;
  return true;
};

// The prelander registration response returns a JWT `accessToken` whose payload
// carries `userId` (the Mongo _id — see backend types/decoded-jwt.type.ts). We
// decode it purely to read that id for the dedupe key; no signature check is
// needed client-side. Returns null if the token is missing/malformed.
export const getUserIdFromAccessToken = (accessToken) => {
  if (!accessToken || typeof accessToken !== 'string') return null;
  const parts = accessToken.split('.');
  if (parts.length < 2) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    const payload = JSON.parse(json);
    return payload?.userId || null;
  } catch {
    return null;
  }
};

// Flatten the GrowthBook flag map into Mixpanel profile properties, mirroring the
// platform's getFormatedUserExperiments (xDate frontend UseMixpanel.tsx): each
// experiment becomes a top-level property, and an object-valued flag carrying a
// `.name` collapses to that name. This is what makes the popup cohort's profile
// look like the control arm's — the platform sets these on identify, but the popup
// non-buyer never reaches the platform, so we replicate it here from the LP.
const formatExperiments = (experiments) => {
  if (!experiments || typeof experiments !== 'object') return {};
  const out = {};
  Object.keys(experiments).forEach((key) => {
    const value = experiments[key];
    out[key] = value && typeof value === 'object' && value.name ? value.name : value;
  });
  return out;
};

// Profile (user-property) update that survives an IMMEDIATE navigation.
// ---------------------------------------------------------------------
// Why not mixpanel.people.set(): the SDK puts every /engage request in its own
// `people` batch queue and flushes it only on the 5s `batch_flush_interval_ms`
// timer. Unlike the EVENTS queue, the people queue is never flushed on
// pagehide/visibilitychange (mixpanel-core.js `flush_on_unload` only touches
// `request_batchers.events`), and its localStorage backlog is retried only on a
// NEXT page load on this origin — which never happens once the user leaves for
// the Upgate checkout. In the `popup_first` arm registration is followed
// immediately by startUpgateCheckout → window.location (well under 5s), so a
// people.set() there was silently dropped and the user properties never arrived.
// (`after_signup` only appeared to work because the user lingers on the popup
// longer than one flush interval.)
//
// So we POST the $set ourselves with navigator.sendBeacon — which the browser
// delivers even after the page is gone — using the same request shape the SDK
// uses (form-encoded `data=<JSON>` body to /engage). Same reasoning as the
// order_created event's send_immediately + sendBeacon below.
const sendProfileUpdate = (userId, props) => {
  if (!props || !Object.keys(props).length) return false;

  // Keep the opt-out parity the SDK's people.set has (addOptOutCheckMixpanelPeople).
  // Nothing on this LP opts users out today, so this is future-proofing: a direct
  // request must not become a way around a consent gate added later.
  try {
    if (mixpanel.has_opted_out_tracking && mixpanel.has_opted_out_tracking()) {
      return false;
    }
  } catch (e) {
    // ignore — treat an unavailable check as "not opted out", like the SDK does
  }

  const payload = {
    $token: TOKEN,
    $distinct_id: userId,
    $user_id: userId,
    $set: props,
  };
  try {
    // Mirrors the SDK's own /engage payload so ID-merge associates this update
    // with the anonymous device profile too. Absent before any persistence
    // exists — $distinct_id alone still lands it on the right profile.
    const deviceId = mixpanel.get_property('$device_id');
    if (deviceId) payload.$device_id = deviceId;
  } catch (e) {
    // ignore — $distinct_id is sufficient
  }

  // registration_started + registration_completed fire back-to-back with the same
  // profile props — send the identical $set only once.
  const encoded = JSON.stringify([payload]);
  if (encoded === lastProfilePayload) return true;
  lastProfilePayload = encoded;

  const url = `${HOST.replace(/\/+$/, '')}/engage/?ip=1&_=${Date.now()}`;
  const body = `data=${encodeURIComponent(encoded)}`;

  try {
    if (
      typeof navigator !== 'undefined' &&
      typeof navigator.sendBeacon === 'function' &&
      navigator.sendBeacon(url, body)
    ) {
      return true;
    }
    if (typeof fetch === 'function') {
      // keepalive keeps the request alive across the unload, like sendBeacon.
      fetch(url, {
        method: 'POST',
        body,
        keepalive: true,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      }).catch(() => {});
      return true;
    }
  } catch (error) {
    console.error('[mixpanel] profile update failed', error);
  }

  // Last resort (no sendBeacon and no fetch): the SDK's batched path. Subject to
  // the drop described above, but better than sending nothing.
  mixpanel.people.set(props);
  return true;
};

// Fire a registration funnel event to Mixpanel for the paywall_popup flow.
// `accessToken` is the JWT returned by the prelander registration response
// (password / magic-link paths). `extraProps` are merged into the event.
// `experiments` is the GrowthBook ab_testing map — set on the PROFILE (not the
// event), mirroring the platform. Returns true when the event was sent.
const trackRegistrationEvent = (
  eventName,
  userId,
  extraProps = {},
  experiments,
  email,
  utm = {},
) => {
  if (!ensureInit()) return false;

  // Without the Mongo _id we can't build the dedupe key, so sending would risk
  // double-counting against the backend's copy. The backend still emits its own
  // copy for this user, so skipping here loses nothing.
  if (!userId) {
    console.warn(`[mixpanel] ${eventName} skipped: no userId`);
    return false;
  }

  try {
    // distinct_id = Mongo _id, matching the backend $user_id and the platform's
    // mixpanel.identify(currentUser._id), so all copies land on one profile.
    mixpanel.identify(userId);
    // Profile enrichment — the experiment variant lives on the PROFILE on the
    // regular flow (platform UseMixpanel.tsx:83 people.set). The popup non-buyer
    // never triggers that, so we set the same experiments here so this cohort is
    // segmentable in Mixpanel just like the control arm. `$email` goes on the
    // PROFILE too: profile updates are last-write-wins (NOT deduped like events),
    // so this reliably attaches the email even though the backend's copy of the
    // registration event may be the one that survives $insert_id deduping.
    if (experiments || email || Object.keys(utm).length) {
      sendProfileUpdate(userId, {
        ...(experiments ? formatExperiments(experiments) : {}),
        ...(email ? { $email: email } : {}),
        // Last-touch UTM/attribution on the profile so this LP cohort is
        // segmentable by acquisition source (the backend never sets UTMs on the
        // profile, so this is purely additive and not subject to event dedup).
        ...utm,
        landing_page_source: LP_SOURCE,
      });
    }
    const insertId = buildInsertId(userId, eventName);
    mixpanel.track(eventName, {
      // Deterministic dedupe key — see the file header. This is what makes the
      // event count once across LP + backend + platform. Spread so an event
      // outside the deduped set sends no $insert_id at all.
      ...(insertId ? { $insert_id: insertId } : {}),
      // Same attribution the GTM/dataLayer events carry (see analytics.js
      // trackEvent) so the Mixpanel copy matches the GTM copy: lp_source +
      // lander-content, with paywall-type passed in via extraProps by the caller.
      lp_source: LP_SOURCE,
      'lander-content': getLanderContent(),
      source: 'manual',
      flow: 'paywall_popup',
      // Also on the event for convenience; the reliable copy is $email on the
      // profile above (this event prop may lose to the backend copy on dedup).
      ...(email ? { email } : {}),
      // UTM/attribution on the event too, matching the GTM/dataLayer copy
      // (createAccount spreads the same getUtmAndEf() there).
      ...utm,
      ...extraProps,
    });
    return true;
  } catch (error) {
    console.error(`[mixpanel] ${eventName} failed`, error);
    return false;
  }
};

// password / magic-link: the Mongo _id is decoded from the prelander JWT accessToken.
// `experiments` is the GrowthBook ab_testing map (set on the profile). `email` is
// the address the user just registered with — set on the profile as `$email`.
export const trackRegistrationStartedMixpanel = (
  accessToken,
  extraProps = {},
  experiments,
  email,
  utm = {},
) =>
  trackRegistrationEvent(
    REGISTRATION_STARTED,
    getUserIdFromAccessToken(accessToken),
    extraProps,
    experiments,
    email,
    utm,
  );

// Google popup return: the LP has the Mongo _id DIRECTLY (from the return URL's
// `uid` param) — there's no JWT there — so this takes the userId, not a token.
// Deduped against the backend's registration_completed via the shared $insert_id.
// (Google returns no email to the LP, so `email` is normally omitted here.)
export const trackRegistrationCompletedMixpanel = (
  userId,
  extraProps = {},
  experiments,
  email,
  utm = {},
) =>
  trackRegistrationEvent(
    REGISTRATION_COMPLETED,
    userId,
    extraProps,
    experiments,
    email,
    utm,
  );

// order_created (checkout opened) — fired from the popup alongside the GTM
// order_created, with the same data. Identified by the Mongo _id so it lands on
// the user's profile (which already holds $email/UTM from registration). Pass the
// event props via `extraProps`; `experiments` is the GrowthBook ab_testing map.
// Returns true when sent.
export const trackOrderCreatedMixpanel = (userId, extraProps = {}, experiments) => {
  if (!ensureInit()) return false;
  if (!userId) {
    console.warn('[mixpanel] order_created skipped: no userId');
    return false;
  }
  try {
    mixpanel.identify(userId);
    // Full experiment set on the PROFILE (people.set), mirroring the registration
    // events, so this cohort stays segmentable in Mixpanel.
    const formattedExperiments = experiments ? formatExperiments(experiments) : {};
    if (Object.keys(formattedExperiments).length) {
      // Beacon-delivered (see sendProfileUpdate) — order_created fires right
      // before the redirect to Upgate, so the SDK's batched people queue would
      // never flush in time.
      sendProfileUpdate(userId, formattedExperiments);
    }
    // The two ticket-required arm flags also go on the EVENT itself, so the
    // checkout event is filterable by A/B arm directly and not only via the
    // profile.
    const eventExperimentProps = {};
    ORDER_CREATED_EVENT_EXPERIMENT_KEYS.forEach((key) => {
      if (formattedExperiments[key] !== undefined) {
        eventExperimentProps[key] = formattedExperiments[key];
      }
    });
    mixpanel.track(
      ORDER_CREATED,
      {
        lp_source: LP_SOURCE,
        'lander-content': getLanderContent(),
        flow: 'paywall_popup',
        ...eventExperimentProps,
        ...extraProps,
      },
      // order_created fires immediately before the redirect to the Upgate hosted
      // checkout. With Mixpanel's default batching (batch_requests:true, 5s flush),
      // a normally-queued event is discarded when the page navigates away before
      // the next flush — which is why order_created never reached Mixpanel. Bypass
      // the queue (send_immediately) and use navigator.sendBeacon, which the browser
      // delivers even after navigation. Unlike registration_started/completed, this
      // event has NO backend copy, so losing it here means losing it entirely.
      { send_immediately: true, transport: 'sendBeacon' },
    );
    return true;
  } catch (error) {
    console.error('[mixpanel] order_created failed', error);
    return false;
  }
};

export default {
  trackRegistrationStartedMixpanel,
  trackRegistrationCompletedMixpanel,
  trackOrderCreatedMixpanel,
  getUserIdFromAccessToken,
  buildInsertId,
};
